import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowUpRight, CalendarClock, Check, ChevronRight, CircleAlert, Clock, FileText, Globe, History, Lock, LogOut, Pencil, Plus, RadioTower, RefreshCw, Send, Target, Trash, TrendingDown, TrendingUp, Wallet, Zap } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import {
  getReferralSettings,
  cancelSignal,
  createSignal,
  deleteSignal,
  getPlatformConfig,
  getSignalMarkets,
  getSignals,
  postSignalToChannel,
  publishSignal,
  testTelegramChannel,
  updateSignal,
  type AdminSignal,
  type SignalAccessKind,
  type SignalInput,
  type SignalMarket,
  type SignalStatus,
} from '../api/admin'
import { PageHeader } from '../components/layout'
import {
  Badge,
  Button,
  Chip,
  EmptyState,
  Field,
  IconButton,
  IdChip,
  Input,
  Modal,
  Pager,
  Panel,
  Progress,
  Select,
  Skeleton,
  Spacer,
  Spinner,
  StatusPill,
  Table,
  TableSkeleton,
  Tabs,
  Toolbar,
  cx,
  describeError,
  money,
  statusLabel,
  tableClass,
  toneClass,
  ui,
  useToast,
  useTotp,
  when,
  type TabOption,
  type Tone,
} from '../components/ui/Ui'
import { SIGNAL_WINDOW_PRESETS, parseSignalCodeTtl, signalCodeTtlMinutes, usePlatformSettings, windowLengthLabel } from '../lib/settings'
import styles from './Signals.module.css'

type Tab = 'upcoming' | 'draft' | 'live' | 'past' | 'all'

const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'live', label: 'В эфире' },
  { value: 'upcoming', label: 'Запланированы' },
  { value: 'draft', label: 'Черновики' },
  { value: 'past', label: 'Прошли' },
  { value: 'all', label: 'Все' },
]

/** The tabs whose first page also gives the counter in the tab («Прошли» is filtered here, so it has none). */
const COUNTED: readonly Tab[] = ['live', 'upcoming', 'draft', 'all']
const COUNT_TONE: Partial<Record<Tab, Tone>> = { live: 'accent', upcoming: 'info' }

const ACCESS_RU: Record<SignalAccessKind, string> = {
  public: 'публичный',
  base: 'базовый',
  premium: 'премиум',
  bonus: 'бонусный',
  agent: 'агентский',
}

/** What an empty tab says, and where to go from there. */
const EMPTY_OF_TAB: Record<Tab, { title: string; description: string; icon: ReactNode }> = {
  live: { title: 'В эфире ничего нет', description: 'Создайте сигнал кнопкой «Новый сигнал» и опубликуйте его.', icon: <RadioTower /> },
  upcoming: { title: 'Запланированных сигналов нет', description: 'Сигнал с выпуском «сейчас» после публикации сразу попадает во вкладку «В эфире».', icon: <CalendarClock /> },
  draft: { title: 'Черновиков нет', description: 'Создайте сигнал кнопкой «Новый сигнал» — он сохранится здесь черновиком.', icon: <FileText /> },
  past: { title: 'Прошедших сигналов пока нет', description: 'Сюда попадают истёкшие и отменённые.', icon: <History /> },
  all: { title: 'Сигналов пока нет', description: 'Создайте первый кнопкой «Новый сигнал».', icon: <RadioTower /> },
}

/**
 * The «Канал» cell's short words for a failed post. The server stores
 * `<reason>: <Telegram's description>`; the full text stays in the tooltip.
 */
const CHANNEL_FAIL_RU: Record<string, string> = {
  not_admin: 'бот не администратор канала',
  not_found: 'канал не найден',
  bad_token: 'токен бота не принят',
  forbidden: 'токен бота не принят',
  network: 'Telegram не ответил',
  other: 'Telegram отказал',
}

const STATUS_OF_TAB: Record<Tab, SignalStatus | undefined> = {
  upcoming: 'scheduled',
  live: 'live',
  draft: 'draft',
  past: undefined,
  all: undefined,
}

const CHAT_TYPE_RU: Record<string, string> = {
  channel: 'канал',
  supergroup: 'супергруппу',
  group: 'группу',
  private: 'личный чат',
}

/** The market the spec names when `signals.default_market` is unset or not in the list. */
const FALLBACK_MARKET = 'XAU/USDT-PERP'

/** How a direction reads: long/short on futures, buy/sell on spot. */
function sideLabel(side: 'buy' | 'sell', marketType: 'spot' | 'futures'): string {
  if (marketType === 'futures') return side === 'buy' ? 'лонг' : 'шорт'
  return side === 'buy' ? 'покупка' : 'продажа'
}

/** Local datetime input value ↔ ISO. */
function toLocal(iso: string): string {
  const date = new Date(iso)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
function fromLocal(value: string): string {
  return new Date(value).toISOString()
}
/** «до 06:16» when the window closes the day it opens; the full date otherwise. */
function untilLabel(releaseAt: string, expiresAt: string): string {
  const expires = new Date(expiresAt)
  if (new Date(releaseAt).toDateString() !== expires.toDateString()) return when(expiresAt)
  return expires.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}
/** «до 11:30» today; the date as well on any other day. */
function endLabel(iso: string): string {
  const end = new Date(iso)
  if (end.toDateString() !== new Date().toDateString()) return when(iso)
  return end.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}
/** A stored window in whole minutes, for «Код действует N мин». */
function windowMinutes(releaseAt: string, expiresAt: string): number {
  return Math.round((Date.parse(expiresAt) - Date.parse(releaseAt)) / 60_000)
}
/** Now, to the second: rounding down to the minute would cut up to a fifth of a five-minute code. */
function nowSecond(): string {
  const date = new Date()
  date.setMilliseconds(0)
  return date.toISOString()
}
/** The clock, re-read every `ms` while `running`: «сейчас» and what is left of a window follow it. */
function useNow(ms: number, running: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!running) return
    const timer = window.setInterval(() => setNow(Date.now()), ms)
    return () => window.clearInterval(timer)
  }, [ms, running])
  return now
}
/** `telegram.channel_access_kinds` as known kinds; null — this desk cannot read it. */
function channelKindsOf(raw: unknown): SignalAccessKind[] | null {
  return Array.isArray(raw) ? raw.filter((kind): kind is SignalAccessKind => typeof kind === 'string' && Object.prototype.hasOwnProperty.call(ACCESS_RU, kind)) : null
}
/** A decimal as the server takes it: operators type «1,5»; empty is null. */
function decimalInput(value: string): string | null {
  return value.replace(',', '.').trim() || null
}
function decimalOk(value: string | null): boolean {
  return value === null || /^\d+(\.\d+)?$/.test(value)
}
/** Why an optional positive amount or percentage is refused; null — it is fine. */
function positiveError(value: string | null): string | null {
  if (value === null) return null
  if (!decimalOk(value)) return 'Число, например 1.5.'
  return Number(value) > 0 ? null : 'Больше нуля или пусто.'
}
/** A signal's size bounds: «10–1 000», «от 10», «до 1 000» or «—». */
function AmountRange({ min, max }: { min: string | null; max: string | null }): JSX.Element {
  if (min !== null && max !== null) return <span className="num">{money(min, 0)}–{money(max, 0)}</span>
  if (min !== null) return <>от <span className="num">{money(min, 0)}</span></>
  if (max !== null) return <>до <span className="num">{money(max, 0)}</span></>
  return <>—</>
}
function randomCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = 'SIG-'
  for (let index = 0; index < 6; index += 1) out += alphabet[Math.floor(Math.random() * alphabet.length)]
  return out
}

/** A span of time as a ticking clock: «04:12», «1:04:12»; past a day, «2 д 3 ч». */
function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const days = Math.floor(total / 86_400)
  const hours = Math.floor((total % 86_400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const pad = (n: number): string => String(n).padStart(2, '0')
  if (days > 0) return `${days} д ${hours} ч`
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`
  return `${pad(minutes)}:${pad(seconds)}`
}

/** Counters for the tabs: the same keys as each tab's first page, so a counter and its list share one request. */
function useTabCounts(): Partial<Record<Tab, number>> {
  const results = useQueries({
    queries: COUNTED.map((tab) => ({
      queryKey: ['signals', tab, 1],
      queryFn: () => getSignals({ page: 1, status: STATUS_OF_TAB[tab] }),
      refetchInterval: 60_000,
    })),
  })
  const counts: Partial<Record<Tab, number>> = {}
  COUNTED.forEach((tab, index) => {
    const total = results[index]?.data?.total
    if (typeof total === 'number') counts[tab] = total
  })
  return counts
}

/**
 * One-shot signals: an operator drafts a trade with a release window and a
 * code, publishes it (second factor), the bot posts the code to the Telegram
 * channel, and users execute it once each at the live feed price — a spot
 * market order, or a futures position with the signal's leverage.
 */
export default function Signals(): JSX.Element {
  const [tab, setTab] = useState<Tab>('live')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<AdminSignal | 'new' | null>(null)
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()
  const settings = usePlatformSettings()
  const channelKinds = channelKindsOf(settings.get('telegram.channel_access_kinds'))
  const counts = useTabCounts()

  const list = useQuery({
    queryKey: ['signals', tab, page],
    queryFn: () => getSignals({ page, status: STATUS_OF_TAB[tab] }),
    refetchInterval: 20_000,
  })
  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['signals'] })

  const lifecycle = useMutation({
    mutationFn: ({ id, action, code }: { id: string; action: 'publish' | 'cancel'; code: string }) =>
      action === 'publish' ? publishSignal(id, code) : cancelSignal(id, code),
    onSuccess: (signal, { action }) => {
      toast(`${signal.code} — ${statusLabel(signal.status)}${action === 'publish' ? `, код действует до ${untilLabel(signal.releaseAt, signal.expiresAt)}` : ''}`)
      refresh()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const channelPost = useMutation({
    mutationFn: ({ row, code }: { row: AdminSignal; code: string }) => postSignalToChannel(row.id, code),
    onSuccess: (result, { row }) => {
      toast(`${row.code} опубликован в канале · ${when(result.postedAt)}`)
      refresh()
    },
    onError: (error) => {
      toast(describeError(error), true)
      // The server keeps the reason on the row («Канал»).
      refresh()
    },
  })

  const rows = (list.data?.items ?? []).filter((row) =>
    tab === 'past' ? row.status === 'expired' || row.status === 'cancelled' : true,
  )
  // A live window counts down and a scheduled one counts to its release: the clock ticks only while one is on screen.
  const ticking = rows.some((row) => row.status === 'live' || row.status === 'scheduled' || row.status === 'draft')
  const now = useNow(1000, ticking)
  const tabOptions: ReadonlyArray<TabOption<Tab>> = TABS.map((item) => ({
    ...item,
    count: counts[item.value] !== undefined && (counts[item.value] ?? 0) > 0 ? counts[item.value] : undefined,
    tone: COUNT_TONE[item.value],
  }))
  const empty = EMPTY_OF_TAB[tab]
  const canCreate = tab === 'live' || tab === 'draft' || tab === 'all'

  return (
    <>
      <PageHeader
        title="Сигналы"
        description="Разовые сделки по коду: сигнал выходит в эфир, бот публикует код в Telegram-канале, каждый исполняет его один раз по цене фида."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
            Новый сигнал
          </Button>
        }
      />
      <ChannelCard />
      <Toolbar className={styles.toolbar}>
        <Tabs value={tab} options={tabOptions} onChange={(next) => { setTab(next); setPage(1) }} ariaLabel="Сигналы по статусу" />
        <Spacer />
        {list.isFetching && !list.isPending ? (
          <span className={styles.syncing} aria-live="polite">
            <Spinner size={12} /> обновление
          </span>
        ) : null}
      </Toolbar>
      <Panel>
        {list.isError ? (
          <EmptyState tone="error" icon={<CircleAlert />} title="Список не загрузился" description={describeError(list.error)} />
        ) : list.isPending ? (
          <TableSkeleton rows={5} headers={['Выпуск', 'Код', 'Сигнал', 'Размер', 'Статус · канал', '']} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={empty.icon}
            title={empty.title}
            description={empty.description}
            action={
              canCreate ? (
                <Button variant="secondary" icon={<Plus />} onClick={() => setEditing('new')}>
                  Новый сигнал
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table ariaLabel="Сигналы">
            <thead>
              <tr>
                <th>Выпуск</th>
                <th>Код</th>
                <th>Сигнал</th>
                <th className={tableClass.right} title="Доля баланса; ниже — границы суммы (на фьючерсе — маржи), USDT">
                  Размер
                </th>
                <th title="Статус сигнала; ниже — его сообщение в Telegram-канале">Статус · канал</th>
                <th>
                  <span className="sr-only">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const marketType = row.marketType === 'futures' ? 'futures' : 'spot'
                const draft = row.status === 'draft'
                return (
                  <tr key={row.id} className={row.status === 'live' ? styles.liveRow : undefined}>
                    <td>
                      <WindowCell row={row} now={now} />
                    </td>
                    <td>
                      <IdChip value={row.code} full label="код" />
                    </td>
                    <td className={styles.signalCell}>
                      <div className={styles.signalTitle}>{row.title}</div>
                      <div className={styles.facts}>
                        <span className={cx(styles.fact, styles.factMono)}>{row.marketSymbol}</span>
                        <span className={cx(styles.fact, row.side === 'buy' ? styles.factBuy : styles.factSell)}>
                          {row.side === 'buy' ? <TrendingUp aria-hidden="true" /> : <TrendingDown aria-hidden="true" />}
                          {sideLabel(row.side, marketType)}
                        </span>
                        {marketType === 'futures' ? <span className={cx(styles.fact, styles.factMono)}>{row.leverage ?? 1}×</span> : null}
                        <span className={styles.access} title="С какого уровня клиент может исполнить">
                          {row.minLevelRank === 0 ? <Globe aria-hidden="true" /> : <Lock aria-hidden="true" />}
                          {row.minLevelRank === 0 ? 'все уровни' : `от ${row.minLevelRank} уровня`}
                        </span>
                      </div>
                    </td>
                    <td className={tableClass.right}>
                      <span className="num">{money(String(Number(row.defaultFraction) * 100), 0)} %</span>
                      <span className={tableClass.sub}>
                        <AmountRange min={row.minAmount} max={row.maxAmount} />
                      </span>
                    </td>
                    <td>
                      <div className={styles.statusCell}>
                        <StatusPill status={row.status} />
                        {row.channelPostedAt ? (
                          <span className={styles.posted} title="Сообщение с кодом есть в канале">
                            <Check aria-hidden="true" />
                            в канале <span className="num">{when(row.channelPostedAt)}</span>
                          </span>
                        ) : row.channelError ? (
                          <span className={styles.channelCellError} title={row.channelError}>
                            <CircleAlert aria-hidden="true" />
                            {CHANNEL_FAIL_RU[row.channelError.split(':', 1)[0] ?? ''] ?? 'не опубликован'}
                          </span>
                        ) : channelKinds !== null && !channelKinds.includes(row.accessKind) ? (
                          <span className={styles.notPosted} title="Этого вида доступа нет в telegram.channel_access_kinds: бот сам его не публикует, кнопка «В канал» опубликует вручную.">
                            в канал не публикуется
                          </span>
                        ) : null}
                        {row.status === 'live' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<Send />}
                            className={styles.channelCellAction}
                            loading={channelPost.isPending && channelPost.variables?.row.id === row.id}
                            title={row.channelPostedAt ? 'Сообщение уже есть в канале — кнопка опубликует его ещё раз' : 'Опубликовать код и условия в Telegram-канале'}
                            onClick={() => void withTotp((code) => channelPost.mutateAsync({ row, code }))}
                          >
                            В канал
                          </Button>
                        ) : null}
                      </div>
                    </td>
                    <td className={tableClass.actions}>
                      <div className={styles.rowActions}>
                        {draft ? (
                          <Button size="sm" variant="primary" onClick={() => void withTotp((code) => lifecycle.mutateAsync({ id: row.id, action: 'publish', code }))}>
                            Опубликовать
                          </Button>
                        ) : null}
                        {row.status === 'scheduled' || row.status === 'live' ? (
                          <Button size="sm" variant="danger" onClick={() => void withTotp((code) => lifecycle.mutateAsync({ id: row.id, action: 'cancel', code }))}>
                            Отменить
                          </Button>
                        ) : null}
                        <IconButton size="sm" icon={draft ? <Pencil /> : <ChevronRight />} label={draft ? 'Изменить' : 'Открыть'} tooltipSide="left" onClick={() => setEditing(row)} />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
        {list.data ? <Pager page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} /> : null}
      </Panel>

      {editing !== null ? (
        <SignalEditor
          signal={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
          onCreated={() => {
            // A new draft lands in «Черновики»: show it there.
            setTab('draft')
            setPage(1)
          }}
        />
      ) : null}
    </>
  )
}

/**
 * «Выпуск»: when the signal goes out and until when its code works; a live
 * window counts down with a bar of what is left, a scheduled one counts to
 * its release, a draft whose window has run out says so before «Опубликовать».
 */
function WindowCell({ row, now }: { row: AdminSignal; now: number }): JSX.Element {
  const release = Date.parse(row.releaseAt)
  const expires = Date.parse(row.expiresAt)
  const left = expires - now
  return (
    <div className={styles.window}>
      <span className={styles.windowWhen}>{when(row.releaseAt)}</span>
      <span className={tableClass.sub}>до {untilLabel(row.releaseAt, row.expiresAt)}</span>
      {row.status === 'live' ? (
        left > 0 ? (
          <span className={styles.countdown}>
            <span className={styles.countdownText}>
              осталось <span className={cx(styles.countdownFigure, 'num')}>{clock(left)}</span>
            </span>
            <Progress value={left} max={Math.max(1, expires - release)} className={styles.countdownBar} label="Осталось от окна кода" />
          </span>
        ) : (
          <span className={styles.windowClosed}>окно закрылось</span>
        )
      ) : row.status === 'scheduled' && release > now ? (
        <span className={styles.untilRelease}>
          через <span className="num">{clock(release - now)}</span>
        </span>
      ) : null}
      {/* Publishing it now would only earn SIGNAL_EXPIRED: say so before the click. */}
      {row.status === 'draft' && expires <= now ? (
        <span className={styles.windowPassed} title="Откройте черновик, нажмите «Сейчас» рядом с выпуском и сохраните">
          <CircleAlert aria-hidden="true" /> окно прошло
        </span>
      ) : null}
    </div>
  )
}

type ChannelState = { tone: Tone; label: string }

/**
 * Where the bot posts codes: the `telegram.signals_channel` setting, and a
 * check that asks Telegram whether the bot can write there right now.
 */
function ChannelCard(): JSX.Element {
  const settings = usePlatformSettings()
  const check = useMutation({ mutationFn: testTelegramChannel })
  const raw = settings.get('telegram.signals_channel')
  const channel = typeof raw === 'string' ? raw.trim() : typeof raw === 'number' ? String(raw) : ''
  const kinds = channelKindsOf(settings.get('telegram.channel_access_kinds'))
  const state: ChannelState | null = check.isError
    ? { tone: 'down', label: 'проверка не прошла' }
    : check.isSuccess
      ? { tone: 'accent', label: 'бот может писать' }
      : !settings.readable
        ? { tone: 'neutral', label: 'задаёт администратор' }
        : !settings.settled
          ? null
          : channel !== ''
            ? { tone: 'info', label: 'задан' }
            : { tone: 'warning', label: 'не задан' }

  return (
    <Panel className={styles.channel}>
      <div className={styles.channelMain}>
        <span className={cx(styles.channelIcon, toneClass(state?.tone ?? 'neutral'))} aria-hidden="true">
          <Send />
        </span>
        <div className={styles.channelText}>
          <div className={styles.channelHead}>
            <h2 className={styles.channelTitle}>Telegram-канал для кодов</h2>
            {state ? (
              <Badge tone={state.tone} size="sm">
                {state.label}
              </Badge>
            ) : (
              <Skeleton width={64} height={20} radius={999} />
            )}
          </div>
          <div className={styles.channelLine}>
            {!settings.readable ? (
              <span>Канал задаёт администратор в «Настройках» (telegram.signals_channel)</span>
            ) : !settings.settled ? (
              <Skeleton width={180} height={14} />
            ) : channel !== '' ? (
              <span className={cx(styles.channelValue, 'num')}>{channel}</span>
            ) : (
              <span>Канал ещё не указан.</span>
            )}
          </div>
        </div>
        <div className={styles.channelActions}>
          {settings.readable && settings.settled ? (
            <Link to="/settings?edit=telegram.signals_channel" className={styles.settingsLink}>
              {channel !== '' ? 'Изменить' : 'Задать в «Настройках»'}
              <ArrowUpRight aria-hidden="true" />
            </Link>
          ) : null}
          <Button
            size="sm"
            icon={<RefreshCw />}
            loading={check.isPending}
            title="Бот отправит в канал короткое проверочное сообщение — его увидят подписчики"
            onClick={() => check.mutate()}
          >
            Проверить канал
          </Button>
        </div>
      </div>
      {/* Always rendered, so a screen reader announces the result when it arrives. */}
      <div role="status" aria-live="polite">
        {check.isSuccess ? (
          <div className={cx(styles.channelResult, styles.channelOk)}>
            <Check aria-hidden="true" />
            <span>
              Бот может писать в {CHAT_TYPE_RU[check.data.chat.type] ?? check.data.chat.type}
              {check.data.chat.title ? ` «${check.data.chat.title}»` : ''} <span className="num">(id {String(check.data.chat.id)})</span>.
            </span>
          </div>
        ) : check.isError ? (
          <div className={cx(styles.channelResult, styles.channelError)}>
            <CircleAlert aria-hidden="true" />
            <span>{describeError(check.error)}</span>
          </div>
        ) : null}
      </div>
      <details className={styles.channelDetails}>
        <summary className={styles.channelDetailsSummary}>
          Как работает публикация
          <ChevronRight aria-hidden="true" />
        </summary>
        <dl className={styles.channelFacts}>
          <div className={styles.channelFact}>
            <dt>Автопубликация</dt>
            <dd>Когда сигнал выходит в эфир, бот сам публикует в канал код и условия — один раз; повтор — кнопкой «В канал» в строке сигнала.</dd>
          </div>
          {kinds !== null ? (
            <div className={styles.channelFact}>
              <dt>Какие сигналы уходят в канал</dt>
              <dd>
                {kinds.length > 0 ? kinds.map((kind) => ACCESS_RU[kind]).join(', ') : 'никакие'} <span className={styles.settingKey}>telegram.channel_access_kinds</span>
              </dd>
            </div>
          ) : null}
          <div className={styles.channelFact}>
            <dt>Проверка</dt>
            <dd>«Проверить канал» отправляет в канал короткое сообщение «Проверка связи…» — его увидят подписчики.</dd>
          </div>
        </dl>
      </details>
    </Panel>
  )
}

/** One block of the signal form: an icon, a title and a line of context over its fields. */
function FormSection({ id, icon, title, description, children }: { id: string; icon: ReactNode; title: string; description?: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <section className={styles.formSection} aria-labelledby={`signal-form-${id}`}>
      <header className={styles.formSectionHead}>
        <span className={styles.formSectionIcon} aria-hidden="true">
          {icon}
        </span>
        <div className={styles.formSectionText}>
          <h3 id={`signal-form-${id}`} className={styles.formSectionTitle}>
            {title}
          </h3>
          {description ? <p className={styles.formSectionDescription}>{description}</p> : null}
        </div>
      </header>
      {children}
    </section>
  )
}

function SignalEditor({ signal, onClose, onSaved, onCreated }: { signal: AdminSignal | null; onClose: () => void; onSaved: () => void; onCreated: () => void }): JSX.Element {
  // The window: the default length (`signals.code_ttl_minutes`) until the operator
  // picks another, up to a day; the save sends the end that follows from it.
  const [form, setForm] = useState<SignalInput>(() => {
    if (signal) {
      return { code: signal.code, title: signal.title, marketId: signal.marketId, side: signal.side, leverage: signal.leverage ?? 1, releaseAt: signal.releaseAt, defaultFraction: signal.defaultFraction, minAmount: signal.minAmount, maxAmount: signal.maxAmount, accessKind: signal.accessKind, minLevelRank: signal.minLevelRank, targetProfitPct: signal.targetProfitPct, takeProfitPct: signal.takeProfitPct, stopLossPct: signal.stopLossPct, maxHoldSec: signal.maxHoldSec }
    }
    return { code: randomCode(), title: '', marketId: 0, side: 'buy', leverage: 1, releaseAt: nowSecond(), defaultFraction: '0.01', minAmount: null, maxAmount: null, accessKind: 'public', minLevelRank: 0, targetProfitPct: null, takeProfitPct: null, stopLossPct: null, maxHoldSec: null }
  })
  // «Сейчас»: the release follows the clock and the save sends the moment of saving,
  // so minutes spent filling the form do not come out of the code's window.
  const [releaseNow, setReleaseNow] = useState(signal === null)
  // The length field as typed: null means the default, which follows the setting
  // as it loads; a saved draft starts from its own window.
  const [lengthText, setLengthText] = useState<string | null>(() =>
    signal === null ? null : String(windowMinutes(signal.releaseAt, signal.expiresAt)),
  )
  // The percent field keeps what the operator typed («0.», «0,5») instead of re-rendering a number.
  const [fractionText, setFractionText] = useState(() => String(Math.round(Number(form.defaultFraction) * 10000) / 100))
  const toast = useToast()
  const settings = usePlatformSettings()
  const platform = useQuery({ queryKey: ['platform-config'], queryFn: getPlatformConfig, staleTime: 60_000 })
  // The ladder itself, so the select names the levels the panel actually has.
  const referral = useQuery({ queryKey: ['referral-settings'], queryFn: getReferralSettings, staleTime: 60_000 })
  // A signal can never be opened to the member rung, so it is not offered.
  const levelOptions = (referral.data?.levels ?? [])
    .filter((level) => level.rank >= 0)
    .map((level) => ({
      rank: level.rank,
      label:
        level.minDepositUsdt !== null
          ? `${level.name} · депозит от ${money(level.minDepositUsdt, 0)} USDT`
          : `${level.name} · от ${level.minQualifiedDirects ?? 0} приглашённых`,
    }))
  const markets = useQuery({ queryKey: ['signal-markets'], queryFn: getSignalMarkets, staleTime: 60_000 })
  const available = markets.data ?? []
  const spot = available.filter((market) => market.marketType === 'spot')
  const futures = available.filter((market) => market.marketType === 'futures')
  const locked = signal !== null && signal.status !== 'draft'
  const now = useNow(1000, !locked)
  const ttl = signalCodeTtlMinutes(platform.data?.signalCodeTtlMinutes)
  const lengthInput = lengthText ?? String(ttl)
  const chosenLength = parseSignalCodeTtl(lengthInput)

  // A new signal starts on the default market: the setting, else gold, else the first one.
  const preferred = settings.get('signals.default_market')
  useEffect(() => {
    if (signal !== null || form.marketId !== 0 || available.length === 0 || !settings.settled) return
    const pick =
      (typeof preferred === 'string' ? available.find((market) => market.symbol === preferred) : undefined) ??
      available.find((market) => market.symbol === FALLBACK_MARKET) ??
      available[0]
    if (pick) setForm((current) => ({ ...current, marketId: pick.id, leverage: 1 }))
  }, [signal, form.marketId, available, settings.settled, preferred])

  const market: SignalMarket | undefined = available.find((item) => item.id === form.marketId)
  const marketType: 'spot' | 'futures' = market?.marketType ?? signal?.marketType ?? 'spot'
  const isFutures = marketType === 'futures'
  const maxLeverage = market?.maxLeverage ?? Math.max(1, form.leverage)
  // An old signal's market may have been switched off since; keep it readable.
  const orphanSymbol = signal !== null && market === undefined && form.marketId === signal.marketId ? signal.marketSymbol : null

  const save = useMutation({
    mutationFn: () => {
      // The end is the release plus the chosen length; «Сейчас» stamps both at the moment of saving.
      const releaseAt = releaseNow ? nowSecond() : form.releaseAt
      const expiresAt = new Date(Date.parse(releaseAt) + (chosenLength ?? ttl) * 60_000).toISOString()
      const body: SignalInput = { ...form, releaseAt, expiresAt, leverage: isFutures ? form.leverage : 1 }
      return signal === null ? createSignal(body) : updateSignal(signal.id, body)
    },
    onSuccess: (saved) => {
      const until = untilLabel(saved.releaseAt, saved.expiresAt)
      toast(signal === null ? `Черновик создан, код действует до ${until}. Опубликуйте его, как только условия окончательны.` : `Сохранено, код действует до ${until}`)
      onSaved()
      if (signal === null) onCreated()
      onClose()
    },
    onError: (error) => toast(describeError(error), true),
  })
  const remove = useMutation({
    mutationFn: () => (signal === null ? Promise.reject(new Error('nothing to delete')) : deleteSignal(signal.id)),
    onSuccess: () => {
      toast('Черновик удалён')
      onSaved()
      onClose()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const set = <K extends keyof SignalInput>(key: K, value: SignalInput[K]): void => setForm((current) => ({ ...current, [key]: value }))
  const setTargetProfit = (value: string | null): void => {
    setForm((current) => ({ ...current, targetProfitPct: value, takeProfitPct: value === null ? current.takeProfitPct : null }))
  }
  const chooseMarket = (id: number): void => {
    const next = available.find((item) => item.id === id)
    setForm((current) => ({
      ...current,
      marketId: id,
      // Spot runs without leverage; a futures market keeps what fits under its cap.
      leverage: next === undefined || next.marketType === 'spot' ? 1 : Math.min(Math.max(1, current.leverage), next.maxLeverage),
    }))
  }
  const changeFraction = (text: string): void => {
    setFractionText(text)
    const percent = Number(text.trim().replace(',', '.'))
    // toFixed keeps a plain decimal: the server refuses «1e-7».
    set('defaultFraction', text.trim() !== '' && Number.isFinite(percent) && percent > 0 && percent <= 100 ? (percent / 100).toFixed(8).replace(/\.?0+$/, '') : '0')
  }
  const fractionPercent = String(Math.round(Number(form.defaultFraction) * 10000) / 100)
  const positionPercent = String(Math.round(Number(fractionPercent) * form.leverage * 100) / 100)
  const fractionError = Number(form.defaultFraction) > 0 && Number(form.defaultFraction) <= 1 ? null : 'Число больше 0 и не больше 100.'
  const leverageError = isFutures && !(Number.isInteger(form.leverage) && form.leverage >= 1 && form.leverage <= maxLeverage) ? `Целое число от 1 до ${maxLeverage}.` : null
  const codeError = /^[A-Z0-9][A-Z0-9_-]{5,31}$/.test(form.code.trim()) ? null : 'Латиница, цифры, «-» и «_», от 6 до 32 символов.'
  const minError = positiveError(form.minAmount)
  const maxError =
    positiveError(form.maxAmount) ??
    (minError === null && form.minAmount !== null && form.maxAmount !== null && Number(form.maxAmount) < Number(form.minAmount) ? 'Не меньше минимума.' : null)
  const targetProfitError =
    form.targetProfitPct === null
      ? 'Укажите запрограммированный доход сигнала.'
      : positiveError(form.targetProfitPct) ?? (form.takeProfitPct !== null ? 'Нельзя вместе с тейк-профитом.' : null)
  const maxHoldError =
    form.maxHoldSec === null
      ? 'Укажите время до автоматического закрытия.'
      : !Number.isInteger(form.maxHoldSec / 60) || form.maxHoldSec < 60 || form.maxHoldSec > 30 * 86_400
        ? 'Целое число минут от 1 до 43 200.'
        : null
  // The server's limits (SIGNAL_EXIT_INVALID): a sell's target below 100 % — lower would be a price of zero.
  const takeProfitError =
    positiveError(form.takeProfitPct) ??
    (form.takeProfitPct === null
      ? null
      : Number(form.takeProfitPct) >= 1000
        ? 'Меньше 1000 %.'
        : form.side === 'sell' && Number(form.takeProfitPct) >= 100
          ? 'У продажи и шорта — меньше 100 %.'
          : null)
  const stopLossError = positiveError(form.stopLossPct) ?? (form.stopLossPct !== null && Number(form.stopLossPct) >= 100 ? 'Меньше 100 %.' : null)
  // Datetime inputs speak the browser's zone; the channel and the app, `signals.display_timezone`.
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const displayZone = platform.data?.timezone
  // A published signal shows what was stored; a draft, what the save will store.
  const releaseAt = releaseNow ? new Date(Math.floor(now / 1000) * 1000).toISOString() : form.releaseAt
  const length = locked ? windowMinutes(signal.releaseAt, signal.expiresAt) : chosenLength ?? ttl
  const endsAt = locked ? signal.expiresAt : new Date(Date.parse(releaseAt) + length * 60_000).toISOString()
  const windowPassed = !locked && Date.parse(endsAt) <= now
  const minutesLeft = Math.ceil((Date.parse(endsAt) - now) / 60_000)
  const releaseHint = locked
    ? 'Когда сигнал вышел или выйдет в эфир.'
    : releaseNow
      ? '«Сейчас» — момент сохранения. Публикуйте сразу: время до публикации вычитается из окна кода.'
      : 'В это время сигнал выйдет в эфир, если к тому моменту опубликован. «Сейчас» — выпуск в момент сохранения.'
  const lengthError = locked || chosenLength !== null ? null : 'Целое число минут: от 1 до 1440 (сутки).'
  const lengthHint = locked
    ? 'Сколько код действовал или будет действовать от выпуска.'
    : `По умолчанию — ${windowLengthLabel(ttl)}. Можно любую, до 24 ч.`
  const windowHint = locked
    ? undefined
    : Date.parse(releaseAt) < now && !releaseNow && !windowPassed
      ? `Выпуск уже наступил: опубликованный сейчас код будет действовать ещё ${windowLengthLabel(minutesLeft)}, а не ${windowLengthLabel(length)}.`
      : 'Выпуск плюс длительность.'
  const valid =
    form.title.trim().length > 0 &&
    form.marketId > 0 &&
    codeError === null &&
    lengthError === null &&
    !windowPassed &&
    fractionError === null &&
    minError === null &&
    maxError === null &&
    targetProfitError === null &&
    maxHoldError === null &&
    takeProfitError === null &&
    stopLossError === null &&
    leverageError === null

  return (
    <Modal
      open
      onClose={onClose}
      title={signal === null ? 'Новый сигнал' : signal.title}
      icon={<RadioTower />}
      subtitle={
        locked
          ? 'Опубликованный сигнал не редактируется: отмените его и создайте новый.'
          : 'Сигнал — обычная сделка по цене фида: на споте рыночный ордер, на фьючерсе позиция с изолированной маржой.'
      }
      wide
      footer={
        <>
          {signal !== null && signal.status === 'draft' ? (
            <Button variant="danger" icon={<Trash />} loading={remove.isPending} onClick={() => remove.mutate()}>
              Удалить черновик
            </Button>
          ) : null}
          <Spacer />
          <Button variant="ghost" onClick={onClose}>
            {locked ? 'Закрыть' : 'Отмена'}
          </Button>
          {locked ? null : (
            <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
              {signal === null ? 'Создать черновик' : 'Сохранить'}
            </Button>
          )}
        </>
      }
    >
      <div className={styles.form}>
        {signal !== null ? (
          <div className={styles.formMeta}>
            <StatusPill status={signal.status} size="sm" />
            <IdChip value={signal.code} full label="код" />
            <span className={styles.formMetaText}>создан {when(signal.createdAt)}</span>
          </div>
        ) : null}

        <FormSection
          id="what"
          icon={<Target />}
          title="Что"
          description="Рынок, направление, плечо и код бот публикует в Telegram-канале и присылает в личные сообщения тем, у кого есть доступ; в приложении условия видны после ввода кода."
        >
          <div className={ui.grid2}>
            <Field label="Название" hint="Так сигнал называется в ленте и в канале, например «Золото 1». Рынок и направление добавятся сами.">
              <Input value={form.title} onChange={(event) => set('title', event.target.value)} disabled={locked} />
            </Field>
            <Field label="Код" hint="Пользователь вводит его на экране «Сигналы» или открывает ссылкой из канала. Регистр не важен." error={locked ? null : codeError}>
              <Input mono maxLength={32} value={form.code} onChange={(event) => set('code', event.target.value.toUpperCase())} disabled={locked} />
            </Field>
            <Field label="Рынок" hint={markets.isError ? describeError(markets.error) : isFutures ? `Бессрочный фьючерс, плечо до ${maxLeverage}×.` : 'Спот: без плеча.'}>
              <Select value={form.marketId} onChange={(event) => chooseMarket(Number(event.target.value))} disabled={locked}>
                <option value={0}>{markets.isPending ? 'загрузка…' : '— выбрать —'}</option>
                {orphanSymbol !== null ? <option value={form.marketId}>{orphanSymbol}</option> : null}
                {futures.length > 0 ? (
                  <optgroup label="Фьючерсы">
                    {futures.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.symbol} · до {item.maxLeverage}×
                      </option>
                    ))}
                  </optgroup>
                ) : null}
                {spot.length > 0 ? (
                  <optgroup label="Спот">
                    {spot.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.symbol}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </Select>
            </Field>
            {isFutures ? (
              <div className={ui.grid2}>
                <Field label="Направление" hint="Лонг — на рост, шорт — на падение.">
                  <Select value={form.side} onChange={(event) => set('side', event.target.value as 'buy' | 'sell')} disabled={locked}>
                    <option value="buy">Лонг (покупка)</option>
                    <option value="sell">Шорт (продажа)</option>
                  </Select>
                </Field>
                <Field label="Плечо" hint={`От 1 до ${maxLeverage}×. Позиция = маржа × плечо.`} error={leverageError}>
                  <Input mono type="number" min={1} max={maxLeverage} step={1} value={String(form.leverage)} onChange={(event) => set('leverage', Math.floor(Number(event.target.value || '0')))} disabled={locked} />
                </Field>
              </div>
            ) : (
              <Field label="Направление" hint="Продажа исполняется только из реального остатка базового актива.">
                <Select value={form.side} onChange={(event) => set('side', event.target.value as 'buy' | 'sell')} disabled={locked}>
                  <option value="buy">Покупка</option>
                  <option value="sell">Продажа</option>
                </Select>
              </Field>
            )}
          </div>
          <Field
            label="Уровень"
            hint="С какого уровня клиент может исполнить сигнал. «0+» — все, кто внёс от 500 USDT; мемберы (депозит меньше 500) сигналов не получают вовсе. Код, опубликованный в канале, видят все в любом случае: уровень решает, исполнится ли сделка. Сколько сигналов исполнять — не ограничено: уровень это порог, а не квота."
          >
            <Select value={form.minLevelRank} onChange={(event) => set('minLevelRank', Number(event.target.value))} disabled={locked} className={styles.accessSelect}>
              {levelOptions.map((level) => (
                <option key={level.rank} value={level.rank}>
                  {level.rank}+ · {level.label}
                </option>
              ))}
            </Select>
          </Field>
        </FormSection>

        <FormSection id="when" icon={<Clock />} title="Когда" description="Код действует заданное время от выпуска, а не от публикации.">
          <div className={ui.grid2}>
            <div className={styles.whenColumn}>
              <Field label="Выпуск" hint={releaseHint}>
                <div className={styles.release}>
                  <Input
                    type="datetime-local"
                    value={toLocal(releaseAt)}
                    onChange={(event) => {
                      // A cleared picker has no time to keep: stay on the current one.
                      if (event.target.value === '') return
                      setReleaseNow(false)
                      set('releaseAt', fromLocal(event.target.value))
                    }}
                    disabled={locked}
                    className={styles.releaseInput}
                  />
                  {locked ? null : (
                    <Button className={styles.releaseNow} icon={<Zap />} aria-pressed={releaseNow} title="Выпуск — в момент сохранения" onClick={() => setReleaseNow(true)}>
                      Сейчас
                    </Button>
                  )}
                </div>
              </Field>
              <Field label="Окончание" hint={windowHint} error={windowPassed ? 'Окно уже прошло — такой сигнал не опубликовать. Нажмите «Сейчас» или поставьте выпуск позже.' : null}>
                <output className={cx(styles.windowInfo, windowPassed ? styles.windowInfoPassed : '')} aria-live="off">
                  <Clock aria-hidden="true" />
                  <span>
                    Код действует <strong>{windowLengthLabel(length)}</strong> — до <strong className="num">{endLabel(endsAt)}</strong>
                  </span>
                </output>
              </Field>
            </div>
            {/* The one-tap lengths sit beside the field, not in it: the field is a label, and a button inside one is a second control in the wrong place. */}
            <div className={styles.length}>
              <Field label="Длительность" hint={lengthHint} error={lengthError}>
                <Input
                  mono
                  type="number"
                  min={1}
                  max={1440}
                  step={1}
                  value={locked ? String(length) : lengthInput}
                  onChange={(event) => setLengthText(event.target.value)}
                  disabled={locked}
                  trailing="мин"
                />
              </Field>
              {locked ? null : (
                <div className={styles.lengthPresets} role="group" aria-label="Длительность одним нажатием">
                  {SIGNAL_WINDOW_PRESETS.map((minutes) => (
                    <Chip key={minutes} pressed={chosenLength === minutes} onClick={() => setLengthText(String(minutes))}>
                      {windowLengthLabel(minutes)}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className={styles.formNote}>
            По умолчанию код действует {windowLengthLabel(ttl)} — это настройка <span className={styles.settingKey}>signals.code_ttl_minutes</span>; этому сигналу можно
            задать любую длительность до 24 ч. Окно идёт от выпуска, а не от публикации: черновик, опубликованный позже выпуска, пробудет в эфире меньше. Время — вашего
            браузера ({browserZone})
            {displayZone !== undefined && displayZone !== browserZone ? `; в канале и в приложении — по ${displayZone}` : ''}.
            {isFutures ? ' Если у человека по этому рынку уже открыта позиция в обратную сторону, сигнал у него не исполнится.' : ''}
          </p>
        </FormSection>

        <FormSection id="size" icon={<Wallet />} title="Размер" description={isFutures ? 'Маржа сделки — эта доля общего баланса клиента на момент исполнения, в границах ниже; клиент сумму не выбирает.' : 'Сумма сделки — эта доля общего баланса клиента на момент исполнения, в границах ниже; клиент сумму не выбирает.'}>
          <div className={ui.grid3}>
            <Field
              label="Доля баланса"
              hint={
                isFutures
                  ? `Доля общего баланса клиента — стоимости всех его счетов в USDT на момент исполнения; это маржа сделки. Сейчас маржа — ${money(fractionPercent, 0)} % баланса, позиция — ${money(positionPercent, 0)} % при плече ${form.leverage}×. По умолчанию 1 %.`
                  : `Сумма сделки — ${money(fractionPercent, 0)} % общего баланса клиента: стоимости всех его счетов в USDT на момент исполнения. По умолчанию 1 %.`
              }
              error={locked ? null : fractionError}
            >
              <Input mono inputMode="decimal" value={fractionText} onChange={(event) => changeFraction(event.target.value)} disabled={locked} trailing="%" />
            </Field>
            <Field label={isFutures ? 'Мин. маржа' : 'Мин. сумма'} hint="Если доля клиента выйдет меньше, сигнал у него не исполнится. Пусто — минимум рынка." error={locked ? null : minError}>
              <Input mono inputMode="decimal" value={form.minAmount ?? ''} onChange={(event) => set('minAmount', decimalInput(event.target.value))} disabled={locked} trailing="USDT" />
            </Field>
            <Field label={isFutures ? 'Макс. маржа' : 'Макс. сумма'} hint="Большую долю срежет до этой суммы. Пусто — без потолка." error={locked ? null : maxError}>
              <Input mono inputMode="decimal" value={form.maxAmount ?? ''} onChange={(event) => set('maxAmount', decimalInput(event.target.value))} disabled={locked} trailing="USDT" />
            </Field>
          </div>
        </FormSection>

        <FormSection id="exit" icon={<LogOut />} title="Доход и закрытие" description="Эти условия видны только в админке. Срок закрытия отсчитывается отдельно для каждого клиента с момента его исполнения.">
          <div className={ui.grid3}>
            <Field
              label="Доход"
              hint={isFutures ? 'Обязательный итог на маржу сигнала. Например 80 %: маржа 10 USDT закроется с +8 USDT независимо от рынка.' : 'Обязательный итог на сумму сигнала. Например 80 %: 10 USDT закроется с +8 USDT независимо от рынка.'}
              error={locked ? null : targetProfitError}
            >
              <Input mono inputMode="decimal" value={form.targetProfitPct ?? ''} onChange={(event) => setTargetProfit(decimalInput(event.target.value))} disabled={locked} trailing="%" />
            </Field>
            <Field
              label="Тейк-профит"
              hint={form.targetProfitPct !== null ? 'Очищен: когда задан «Доход», это не рыночный тейк, а запрограммированный итог сигнала.' : isFutures ? 'Движение цены от входа, не доход на маржу: при плече 10× 1 % цены — это 10 % маржи. Пусто — без цели.' : 'Ставится на позицию при исполнении. Пусто — без цели.'}
              error={locked ? null : takeProfitError}
            >
              <Input mono inputMode="decimal" value={form.takeProfitPct ?? ''} onChange={(event) => set('takeProfitPct', decimalInput(event.target.value))} disabled={locked || form.targetProfitPct !== null} trailing="%" />
            </Field>
            <Field label="Стоп-лосс" hint={isFutures ? 'Тоже от цены входа. Пусто — без стопа (остаётся ликвидация).' : 'Пусто — без стопа.'} error={locked ? null : stopLossError}>
              <Input mono inputMode="decimal" value={form.stopLossPct ?? ''} onChange={(event) => set('stopLossPct', decimalInput(event.target.value))} disabled={locked} trailing="%" />
            </Field>
            <Field label="Закрыть через" hint="Отсчёт начинается после исполнения сигнала у конкретного клиента. Это время ему не показывается." error={locked ? null : maxHoldError}>
              <Input mono type="number" min={1} max={43_200} step={1} value={form.maxHoldSec === null ? '' : String(Math.round(form.maxHoldSec / 60))} onChange={(event) => set('maxHoldSec', event.target.value === '' ? null : Number(event.target.value) * 60)} disabled={locked} trailing="мин" />
            </Field>
          </div>
        </FormSection>
      </div>
    </Modal>
  )
}
